import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import WorkspaceLoader from './WorkspaceLoader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, FileText, Sparkles, LayoutDashboard, Calendar, Clock, ArrowRight, BookOpen, Layers, Search, Filter, ChevronLeft, ChevronRight, Image as ImageIcon, ArrowUp, ArrowDown, X, GripVertical, Check, ArrowLeft, Bookmark } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import AssessmentPreview from './AssessmentPreview';
import { Eye, Edit, MoreVertical } from 'lucide-react';
import { db, auth, storage } from "@/firebase/config";
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { DiagramBlock } from './components/DiagramBlock';
import { GeneratorView, SectionState } from './IndependentResearchView';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, doc, setDoc, getDoc, getDocs, deleteDoc, serverTimestamp, query, orderBy, limit, startAfter } from "firebase/firestore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AssessmentCreator } from './assessment/components/AssessmentCreator';
import { DedicatedAssessmentEditor } from './assessment/components/DedicatedAssessmentEditor';

// Types for the assessment tool
type AssessmentType = 'CAT' | 'Assignment' | 'Exam' | 'Quiz' | 'Custom';
type QuestionType = 'Multiple Choice' | 'Short Answer' | 'Essay' | 'True/False' | 'Section';

interface SubQuestion {
  id: string;
  type: QuestionType; // Re-use the main QuestionType
  text: string;
  marks: number;
  options?: string[] | null; // For MCQ within sub-question
  correctAnswerIndex?: number; // Correct option index
  modelAnswer?: string | null; // Correct answer text or explanation
}

interface Question {
  id: string;
  type: QuestionType;
  text: string;
  marks: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  options?: string[] | null; // For MCQ
  correctAnswerIndex?: number; // Correct option index
  modelAnswer?: string | null;
  learningObjective?: string;
  image?: string | null; // For image attachments
  subQuestions?: SubQuestion[] | null;
  section?: string; // e.g. "Section A"
}

const MOCK_CALCULUS_ASSESSMENT = {
    id: '1', 
    title: 'Calculus: First Derivatives', 
    type: 'CAT' as AssessmentType, 
    questions: [
        { 
            id: 'q1', type: 'Multiple Choice' as QuestionType, text: 'What is the derivative of f(x) = x^3?', marks: 2, difficulty: 'Easy', 
            options: ['3x^2', 'x^2', '3x', 'x^3'], correctAnswerIndex: 0, 
            section: 'Section A' 
        },
        { 
            id: 'q2', type: 'Short Answer' as QuestionType, text: 'Explain the chain rule in your own words.', marks: 4, difficulty: 'Medium', 
            section: 'Section A', modelAnswer: 'Function of a function derivative...' 
        },
        { 
            id: 'q3', type: 'Short Answer' as QuestionType, text: 'Find the derivative of the following functions:', marks: 6, difficulty: 'Hard', 
            section: 'Section B',
            subQuestions: [
                { id: 'sq1', type: 'Short Answer' as QuestionType, text: 'f(x) = sin(x)', marks: 3 },
                { id: 'sq2', type: 'Short Answer' as QuestionType, text: 'f(x) = e^2x', marks: 3 }
            ]
        }
    ],
    marks: 12, 
    duration: '45 Mins'
};

const splitTextByDiagrams = (text: string) => text.split(/(%%DIAGRAM%%.*?%%)/s).filter(Boolean);

const MarkdownText = ({ children, className }: { children?: string; className?: string }) => {
    const textToProcess = typeof children === 'string' ? children : String(children || '');
    const processed = textToProcess
        .replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => `$${inner}$`)
        .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$${inner}$$`);

    const parts = splitTextByDiagrams(processed);

    return (
        <div className={`markdown-body ${className || ''}`}>
            {parts.map((part, idx) => {
                if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
                    try {
                        const spec = JSON.parse(part.slice(11, -2).trim());
                        return (
                            <div key={idx} className="flex justify-center my-4">
                                <div className="max-w-[400px] w-full">
                                    <DiagramBlock spec={spec} />
                                </div>
                            </div>
                        );
                    } catch {
                        return <span key={idx} className="text-xs text-red-400">[Diagram error]</span>;
                    }
                }
                return (
                    <ReactMarkdown key={idx} remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {part}
                    </ReactMarkdown>
                );
            })}
        </div>
    );
};

const IndependentAssessmentView = () => {
  const location = useLocation();
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  
  // --- VIEW STATE ---
  const [view, setView] = useState<'dashboard' | 'create' | 'preview' | 'ai-generator' | 'dedicated-editor'>('dashboard');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creationMode, setCreationMode] = useState<'manual' | 'ai'>('manual');
  const [previewData, setPreviewData] = useState<any>(null); // State for the preview component
  const [generatorMode, setGeneratorMode] = useState<'manual' | 'ai'>('ai');
  const [examPaper, setExamPaper] = useState<any>(null);
  const [beta2AssessmentId, setBeta2AssessmentId] = useState<string | null>(null);
  const [beta2InitialAssessment, setBeta2InitialAssessment] = useState<any | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'All' | 'Published' | 'Drafts' | 'Bookmarked'>('All');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchFilter), 300);
    return () => clearTimeout(t);
  }, [searchFilter]);

  useEffect(() => {
    if (debouncedSearch) {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>([]);

  const toggleBookmark = async (id: string) => {
    const isBookmarked = bookmarkedIds.includes(id);
    const newBookmarkedIds = isBookmarked ? bookmarkedIds.filter(x => x !== id) : [...bookmarkedIds, id];
    setBookmarkedIds(newBookmarkedIds);

    if (auth.currentUser) {
      try {
        const docRef = doc(db, `teaching_workspaces/${auth.currentUser.uid}/bookmarks/assessments`);
        await setDoc(docRef, { ids: newBookmarkedIds }, { merge: true });
      } catch (e) {
        console.error("Error saving bookmark:", e);
      }
    }
  };

  useEffect(() => {
    const loadBookmarks = async () => {
      if (!auth.currentUser) return;
      try {
        const docRef = doc(db, `teaching_workspaces/${auth.currentUser.uid}/bookmarks/assessments`);
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().ids) {
          setBookmarkedIds(snap.data().ids);
        }
      } catch (e) {
        console.error("Error loading bookmarks:", e);
      }
    };
    if (view === 'dashboard') {
      loadBookmarks();
    }
  }, [view]);

  // --- CREATE FORM STATE ---
  const [assessmentType, setAssessmentType] = useState<AssessmentType>('CAT');
  const [customAssessmentName, setCustomAssessmentName] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState('40 Mins');
  const [instructions, setInstructions] = useState('Answer all questions in the spaces provided.');
  const [questions, setQuestions] = useState<Question[]>([
    { id: '1', type: 'Multiple Choice', text: '', marks: 2, difficulty: 'Easy', options: ['', '', '', ''], learningObjective: '' }
  ]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeImageUploadId, setActiveImageUploadId] = useState<string | null>(null);
  
  // --- STRUCTURE STATE ---
  const [structure, setStructure] = useState([{ name: 'Section A', count: 1 }]); // Default one section

  const generateStructureQuestions = (currentStructure: typeof structure) => {
      // Re-generate questions based on structure, preserving existing data where possible
      let newQuestions: Question[] = [];
      let globalIndex = 0;

      currentStructure.forEach(sect => {
          for(let i=0; i<sect.count; i++) {
              // Try to find an existing question at this "slot" to preserve its text/data
              // Strategy: Look at the current questions array. 
              // If we are expanding, we create new blank ones. If shrinking, we drop end ones.
              // To enable "live" feel without deleting text while typing, we need a smarter merge.
              // For simplicity in this "slot" model: We will just preserve by index if possible, or create fresh.
              
              // ACTUALLY: A better UX for "slots" is:
              // If we have a question at this global index, keep it (and update its section tag).
              // If not, create a new one.
              
              const existingQ = questions[globalIndex];
              
              if (existingQ) {
                 newQuestions.push({
                     ...existingQ,
                     section: sect.name // Ensure section name is sync'd
                 });
              } else {
                 newQuestions.push({
                    id: Math.random().toString(36).substr(2, 9),
                    type: 'Short Answer',
                    text: '',
                    marks: 2,
                    difficulty: 'Medium',
                    section: sect.name,
                    learningObjective: ''
                 });
              }
              globalIndex++;
          }
      });
      
      setQuestions(newQuestions);
  };

  const updateStructure = (index: number, field: 'name' | 'count', value: any) => {
    let finalValue = value;
    if (field === 'count') {
        finalValue = Math.min(100, Math.max(1, Number(value)));
    }

    const newStructure = [...structure];
    newStructure[index] = { ...newStructure[index], [field]: finalValue };
    setStructure(newStructure);
    
    // Auto-update questions live
    let generated: Question[] = [];
    let qIdx = 0;
    
    newStructure.forEach(sect => {
        for(let i=0; i<sect.count; i++) {
            const existingQ = questions[qIdx]; // Use CURRENT state questions
            if (existingQ) {
                 generated.push({ ...existingQ, section: sect.name });
            } else {
                 generated.push({
                    id: Math.random().toString(36).substr(2, 9),
                    type: 'Short Answer',
                    text: '',
                    marks: 2,
                    difficulty: 'Medium',
                    section: sect.name,
                    learningObjective: ''
                 });
            }
            qIdx++;
        }
    });
    setQuestions(generated);
  };

  const addStructureRow = () => {
    const newStruct = [...structure, { name: `Section ${String.fromCharCode(65 + structure.length)}`, count: 1 }];
    setStructure(newStruct);
    
    // Trigger update
    let generated: Question[] = [];
    let qIdx = 0;
    newStruct.forEach(sect => {
        for(let i=0; i<sect.count; i++) {
            const existingQ = questions[qIdx];
             if (existingQ) {
                 generated.push({ ...existingQ, section: sect.name });
            } else {
                 generated.push({
                    id: Math.random().toString(36).substr(2, 9),
                    type: 'Short Answer',
                    text: '',
                    marks: 2,
                    difficulty: 'Medium',
                    section: sect.name,
                    learningObjective: ''
                 });
            }
            qIdx++;
        }
    });
    setQuestions(generated);
  };

  const removeStructureRow = (index: number) => {
    if (structure.length > 1) {
       const newStruct = structure.filter((_, i) => i !== index);
       setStructure(newStruct);
       
        // Trigger update
        let generated: Question[] = [];
        let qIdx = 0;
        newStruct.forEach(sect => {
            for(let i=0; i<sect.count; i++) {
                // When deleting a section (e.g. Section A), do we want to shift Section B's questions into Section A's slots?
                // OR do we want to delete Section A's questions?
                // The "Slot" metaphor implies preserving the *list*.
                // However, usually deleting a section means deleting its content.
                // Let's assume we preserve top-down for now to be safe, or we can filter.
                // Actually, for "Structure", removing a row usually means "exclude those questions".
                // But preserving text is tricky if we just shift indices.
                // Let's iterate linearly for now - it might shift content from Sec B to Sec A, which is maybe weird, but standard for "slots".
                // If user wants to delete Section A, they might expect Section B to become the new first section.
                
                const existingQ = questions[qIdx]; 
                 if (existingQ) {
                     generated.push({ ...existingQ, section: sect.name });
                } else {
                     generated.push({
                        id: Math.random().toString(36).substr(2, 9),
                        type: 'Short Answer',
                        text: '',
                        marks: 2,
                        difficulty: 'Medium',
                        section: sect.name,
                        learningObjective: ''
                     });
                }
                qIdx++;
            }
        });
        setQuestions(generated);
    }
  };

  // --- HANDLERS ---
  const addQuestion = () => {
    const newQ: Question = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'Short Answer',
      text: '',
      marks: 2,
      difficulty: 'Medium',
      learningObjective: ''
    };
    setQuestions([...questions, newQ]);
  };


  const addSubQuestion = (qId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId) {
           const newSub: SubQuestion = {
              id: Math.random().toString(36).substr(2, 9),
              type: 'Short Answer',
              text: '',
              marks: 1,
              options: ['', '', '', ''] // Default options if they switch to MCQ
           };
           const newSubQuestions = [...(q.subQuestions || []), newSub];
           // Auto-calc total marks
           const newTotal = newSubQuestions.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0);
           
           return { 
               ...q, 
               marks: newTotal,
               subQuestions: newSubQuestions 
           };
        }
        return q;
     }));
  };

  const updateSubQuestion = (qId: string, subId: string, field: keyof SubQuestion, value: any) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           const updatedSubs = q.subQuestions.map(sq => sq.id === subId ? { ...sq, [field]: value } : sq);
           
           // Recalculate parent marks if we changed marks
           let newMarks = q.marks;
           if (field === 'marks') {
               newMarks = updatedSubs.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0);
           }
           
           return {
              ...q,
              marks: newMarks,
              subQuestions: updatedSubs
           };
        }
        return q;
     }));
  };

  const removeSubQuestion = (qId: string, subId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           const remainingSubs = q.subQuestions.filter(sq => sq.id !== subId);
           // Recalculate
           const newMarks = remainingSubs.length > 0 
                ? remainingSubs.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0)
                : 2; // Default back to 2 if no subquestions left
                
           return { ...q, marks: newMarks, subQuestions: remainingSubs };
        }
        return q;
     }));
  };

  // Sub-Question Option Helpers
  const addSubOption = (qId: string, subId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId) {
                    return { ...sq, options: [...(sq.options || []), ''] };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const updateSubOption = (qId: string, subId: string, optIndex: number, value: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId && sq.options) {
                    const newOpts = [...sq.options];
                    newOpts[optIndex] = value;
                    return { ...sq, options: newOpts };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const removeSubOption = (qId: string, subId: string, optIndex: number) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId && sq.options && sq.options.length > 2) {
                    return { ...sq, options: sq.options.filter((_, i) => i !== optIndex) };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const updateQuestion = (id: string, field: keyof Question, value: any) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === questions.length - 1) return;
    
    const newQuestions = [...questions];
    const temp = newQuestions[index];
    newQuestions[index] = newQuestions[index + (direction === 'up' ? -1 : 1)];
    newQuestions[index + (direction === 'up' ? -1 : 1)] = temp;
    setQuestions(newQuestions);
  };

  // MCQ Helpers
  const addOption = (qId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options) {
        return { ...q, options: [...q.options, ''] };
      }
      return q;
    }));
  };

  const removeOption = (qId: string, optIndex: number) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options && q.options.length > 2) {
        return { ...q, options: q.options.filter((_, i) => i !== optIndex) };
      }
      return q;
    }));
  };

  const updateOption = (qId: string, optIndex: number, value: string) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options) {
        const newOpts = [...q.options];
        newOpts[optIndex] = value;
        return { ...q, options: newOpts };
      }
      return q;
    }));
  };

  // Image Helper
  const triggerImageUpload = (qId: string) => {
    setActiveImageUploadId(qId);
    fileInputRef.current?.click();
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeImageUploadId && auth.currentUser) {
      try {
          const storageRef = ref(storage, `teaching_workspaces/${auth.currentUser.uid}/assessment_images/${activeImageUploadId}_${Date.now()}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          updateQuestion(activeImageUploadId, 'image', downloadURL);
          setActiveImageUploadId(null);
          // Optional: Show success feedback
      } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
      }
    }
  };

  const totalMarks = questions.reduce((sum, q) => {
    const qMarks = Number(q.marks) || 0;
    const subMarks = q.subQuestions?.reduce((s, sq) => s + (Number(sq.marks) || 0), 0) || 0;
    // If we have subquestions, we likely want to count them instead of the main question marks, or add them.
    // Standard practice: if subquestions exist, the main question mark is usually 0 or ignored, but let's sum both to be safe/flexible.
    return sum + (q.subQuestions && q.subQuestions.length > 0 ? subMarks : qMarks);
  }, 0);

  // --- MOCK DATA HANDLERS ---
  // Helper function for sanitization
  const stripHtml = (html: string) => {
      if (!html) return "";
      return html.replace(/<[^>]*>?/gm, '');
  };

  // Format model answer for Mark Scheme: Answer in orange, Explanation in green
  const formatMarkSchemeAnswer = (text: string | null | undefined): string => {
      if (!text) return '<span class="text-gray-400 italic">No model answer provided.</span>';
      
      let content = String(text).trim();
      
      if (!content) {
          return '<span class="text-gray-400 italic">No model answer provided.</span>';
      }
      
      // Split content into answer and explanation parts
      // Look for first "Step" to separate answer from explanation
      const stepMatch = content.match(/Step\s+\d+/i);
      let answerPart = '';
      let explanationPart = '';
      
      if (stepMatch && stepMatch.index !== undefined && stepMatch.index > 0) {
          answerPart = content.substring(0, stepMatch.index).trim();
          explanationPart = content.substring(stepMatch.index).trim();
      } else if (stepMatch && stepMatch.index === 0) {
          // Content starts with Step - try to find a final result in the steps
          // Look for patterns like "Final:", "Result:", "Answer:", "Therefore:", "dy/dx =", "= " at end
          const finalMatch = content.match(/(?:Final|Result|Answer|Therefore|Hence)[:\s]+([^\n]+)/i) 
                          || content.match(/(?:dy\/dx|f'?\(x\)|y')\s*=\s*([^\n\[\(]+)/i);
          if (finalMatch && finalMatch[1]) {
              answerPart = finalMatch[1].replace(/\[.*marks.*\]/i, '').trim();
          } else {
              answerPart = 'See steps below';
          }
          explanationPart = content;
      } else {
          // No steps found - treat first line as answer, rest as explanation
          const lines = content.split(/\n+/).filter(l => l.trim());
          answerPart = lines[0] || '';
          explanationPart = lines.slice(1).join('\n');
      }
      
      let html = '';
      
      // Answer in orange box
      if (answerPart) {
          html += `<div class="text-orange-600 dark:text-orange-400 font-semibold p-2 bg-orange-50 dark:bg-orange-900/20 rounded border-l-4 border-orange-400 mb-2 overflow-x-auto break-words">${answerPart}</div>`;
      }
      
      // Explanation in green box - split by "Step X" to put each on new line
      if (explanationPart) {
          // Split by Step patterns and format each step on its own line
          const stepParts = explanationPart.split(/(Step\s+\d+:?)/i).filter(s => s.trim());
          let formattedSteps: string[] = [];
          
          for (let i = 0; i < stepParts.length; i++) {
              const part = stepParts[i].trim();
              if (/^Step\s+\d+:?$/i.test(part) && stepParts[i + 1]) {
                  // This is a step header, combine with next part
                  formattedSteps.push(`<span class="font-bold text-green-700 dark:text-green-300">${part}</span> ${stepParts[i + 1].trim()}`);
                  i++; // Skip next part since we combined it
              } else if (!/^Step\s+\d+:?$/i.test(part)) {
                  formattedSteps.push(part);
              }
          }
          
          const explanationHtml = formattedSteps.join('<br/><br/>');
          html += `<div class="text-green-800 dark:text-green-300 text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded border-l-4 border-green-400 overflow-x-auto break-words">${explanationHtml}</div>`;
      }
      
      // If nothing was generated, just show content in orange
      if (!html) {
          html = `<div class="text-orange-600 dark:text-orange-400 font-medium">${content}</div>`;
      }
      
      return html;
  };

  const handleEdit = (id: string) => {
      const found = assessments.find(a => a.id === id);
      if (found) {
          if (found.creatorVersion === 'beta2') {
              setBeta2AssessmentId(id);
              setBeta2InitialAssessment(found);
              setView('dedicated-editor');
              return;
          }

          setTitle(found.title);
          setAssessmentType(found.type);
          
          // Sanitize existing questions on load to fix any pre-existing corrupted data
          const sanitizedQuestions = (found.questions || []).map((q: Question) => ({
              ...q,
              text: stripHtml(q.text),
              options: q.options?.map(opt => stripHtml(opt)),
              modelAnswer: stripHtml(q.modelAnswer || ''),
              subQuestions: q.subQuestions?.map(sq => ({
                  ...sq,
                  text: stripHtml(sq.text),
                  options: sq.options?.map((opt: string) => stripHtml(opt)),
                  modelAnswer: stripHtml(sq.modelAnswer || '')
              }))
          }));

          setQuestions(sanitizedQuestions);
          setStructure(found.structure || [{ name: 'Section A', count: 1 }]);
          setInstructions(found.instructions || '');
          setDuration(found.duration || '40 Mins');
          setEditingId(id);
          setView('create');
          setCreationMode('manual'); // Ensure we are in manual mode to see the inputs
      }
  };

  const handlePreview = (id: string) => {
      const found = assessments.find(a => a.id === id);
      if (found) {
         setPreviewData({
             ...found,
         description: found.instructions,
             totalMarks: found.totalMarks,
             questions: found.questions
         });
         setView('preview');
      }
  };

  // --- FIREBASE INTEGRATION ---
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [firstVisible, setFirstVisible] = useState<any>(null);
  const [pageHistory, setPageHistory] = useState<any[]>([]);
  const assessmentsPerPage = 12;

  // Fetch Assessments (paginated)
  useEffect(() => {
    const fetchAssessments = async () => {
      if (!auth.currentUser) return;
      setLoading(true);
      try {
        let q;
        if (debouncedSearch.trim() !== '' || activeFilter !== 'All') {
          q = query(
            collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`),
            orderBy('createdAt', 'desc'),
            limit(200)
          );
        } else if (currentPage === 1) {
          q = query(
            collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`),
            orderBy('createdAt', 'desc'),
            limit(assessmentsPerPage)
          );
        } else if (lastVisible) {
          q = query(
            collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`),
            orderBy('createdAt', 'desc'),
            startAfter(lastVisible),
            limit(assessmentsPerPage)
          );
        } else {
          setLoading(false);
          return;
        }
        const querySnapshot = await getDocs(q);
        const fetched = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAssessments(fetched);
        setFirstVisible(querySnapshot.docs[0]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        if (currentPage === 1) {
          setPageHistory([querySnapshot.docs[0]]);
        } else {
          setPageHistory(prev => {
            const newHistory = [...prev];
            newHistory[currentPage - 1] = querySnapshot.docs[0];
            return newHistory;
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Error loading assessments:", error);
        setLoading(false);
      }
    };
    if (view === 'dashboard') {
        fetchAssessments();
    }
  }, [view, currentPage, debouncedSearch, activeFilter]);

  // Auto-open assessment if navigated from search utility
  useEffect(() => {
    if (assessments.length > 0 && location.state?.openAssessmentId && !hasAutoOpened) {
      setHasAutoOpened(true);
      // Clear the state so it doesn't re-trigger on refresh
      window.history.replaceState({}, document.title);
      // The user wants to see the exact questions they just made, so we open in preview mode
      handlePreview(location.state.openAssessmentId);
    }
  }, [assessments, location.state, hasAutoOpened]);

  const handleQuestionsGenerated = (aiQuestions: any[], aiParams: any) => {
      // AI generation now outputs questions in manual mode format directly
      // The questions already have: id, type, text, marks, difficulty, options, correctAnswerIndex, modelAnswer, section
      
      // Ensure all required fields are present and properly typed
      const mappedQuestions: Question[] = aiQuestions.map((q: any) => ({
          id: q.id || Math.random().toString(36).substr(2, 9),
          type: q.type || 'Short Answer',
          text: q.text || '',
          marks: Number(q.marks) || 2,
          difficulty: q.difficulty || 'Medium',
          options: q.options || null,
          correctAnswerIndex: q.correctAnswerIndex,
          modelAnswer: q.modelAnswer || null,
          learningObjective: q.learningObjective || 'AI Generated',
          image: q.image || null,
          subQuestions: q.subQuestions || null,
          section: q.section || 'Section A',
      }));

      setQuestions(mappedQuestions);
      setTitle(aiParams.examTitle || `${aiParams.subject}: ${(aiParams.topics || []).join(', ')}`);
      setInstructions("Answer all questions.");
      setStructure([{ name: 'Section A', count: mappedQuestions.length }]);
      
      // Switch to manual mode to let user review/edit
      setCreationMode('manual');
  };

  const handleSave = async () => {
      if (!auth.currentUser) {
          alert("You must be logged in to save.");
          return;
      }
      if (!title.trim()) {
          alert("Please enter a title.");
          return;
      }

      setLoading(true);
      try {
          // Helper function to recursively remove undefined values
          const removeUndefined = (obj: any): any => {
              if (obj === null || obj === undefined) return null;
              if (Array.isArray(obj)) {
                  return obj.map(item => removeUndefined(item));
              }
              if (typeof obj === 'object') {
                  const cleaned: any = {};
                  for (const key in obj) {
                      if (Object.prototype.hasOwnProperty.call(obj, key)) {
                          const value = obj[key];
                          if (value !== undefined) {
                              cleaned[key] = removeUndefined(value);
                          } else {
                              cleaned[key] = null; // Convert undefined to null for Firestore
                          }
                      }
                  }
                  return cleaned;
              }
              return obj;
          };

          // Sanitize questions to remove undefined values for Firestore
          const sanitizedQuestions = questions.map(q => removeUndefined(q));

          const assessmentData = {
              title,
              type: assessmentType,
              instructions,
              structure,
              questions: sanitizedQuestions,
              totalMarks,
              duration, 
              status: 'Draft',
              updatedAt: serverTimestamp()
          };

          if (editingId) {
             // Update existing
             const docRef = doc(db, `teaching_workspaces/${auth.currentUser.uid}/assessments/${editingId}`);
             await setDoc(docRef, assessmentData, { merge: true });
          } else {
             // Create new
             const newRef = doc(collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`));
             await setDoc(newRef, {
                 ...assessmentData,
                 createdAt: serverTimestamp() // Only set created at on new
             });
          }
          
          alert("Assessment Saved Successfully!");
          setEditingId(null);
          setView('dashboard');
      } catch (error) {
          console.error("Error saving assessment:", error);
          alert("Failed to save.");
      } finally {
          setLoading(false);
      }
  };
  
    // --- DASHBOARD VIEW RENDER FUNCTION ---
  const renderDashboardView = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 dark:border-white/5 pb-6">
        <div>
          <h1 className="font-serif italic lowercase tracking-tight text-2xl font-bold flex items-center gap-2"
              style={{ color: 'rgb(249,115,22)' }}>
            <LayoutDashboard className="w-5 h-5 text-blue-600" />
            Assessments
          </h1>
          <p className="text-xs text-muted-foreground">Manage and track student evaluations across all subjects.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search assessments..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full h-10 pl-9 pr-4 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-gray-100 placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
            />
          </div>

          <Button 
            onClick={() => {
                setBeta2AssessmentId(null);
                setBeta2InitialAssessment(null);
                setView('ai-generator');
            }}
            className="bg-blue-600 text-white hover:bg-blue-700 h-10 px-5 rounded-lg shadow-sm flex items-center gap-2 font-semibold transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">New Assessment</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Created', filterMode: 'All', value: assessments.length.toString(), icon: FileText, color: 'text-blue-600' },
          { label: 'Published', filterMode: 'Published', value: assessments.filter(a => a.status === 'Published').length.toString(), icon: Sparkles, color: 'text-green-600' },
          { label: 'Drafts', filterMode: 'Drafts', value: assessments.filter(a => a.status !== 'Published').length.toString(), icon: Clock, color: 'text-amber-600' },
          { label: 'Bookmarked', filterMode: 'Bookmarked', value: bookmarkedIds.length.toString(), icon: Bookmark, color: 'text-amber-500' },
        ].map((stat, i) => (
          <Card 
            key={i} 
            onClick={() => setActiveFilter(activeFilter === stat.filterMode ? 'All' : stat.filterMode as any)}
            className={`border-gray-100 dark:border-white/5 shadow-sm cursor-pointer transition-all ${
                activeFilter === stat.filterMode && stat.filterMode !== 'All' 
                ? 'ring-2 ring-blue-500 bg-blue-50/10 dark:bg-blue-900/20' 
                : 'bg-white dark:bg-white/5 hover:border-blue-300'
            }`}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                <p className="text-xl font-bold">{stat.value}</p>
              </div>
              <stat.icon className={`w-5 h-5 ${stat.color} opacity-40`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2 uppercase tracking-tight">
            Recent Assessments
          </h2>
        </div>
        
        {loading ? (
             <WorkspaceLoader minHeight="min-h-[200px]" />
        ) : assessments.length === 0 ? (
             <div className="text-center py-20 bg-gray-50/50 dark:bg-white/5 rounded-xl border-2 border-dashed border-gray-200 dark:border-white/10 flex flex-col items-center justify-center gap-4">
                 <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-muted-foreground opacity-50" />
                 </div>
                 <div className="space-y-1">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">No assessments found</h3>
                    <p className="text-muted-foreground font-medium text-sm">Get started by creating your first assessment.</p>
                 </div>
                 <Button 
                    variant="outline" 
                    className="mt-2 bg-transparent border-primary/20 text-primary hover:bg-primary/10"
                    onClick={() => {
                        setTitle('');
                        setAssessmentType('CAT');
                        setQuestions([{ id: '1', type: 'Short Answer', text: '', marks: 2, difficulty: 'Easy', options: [], learningObjective: '' }]);
                        setStructure([{ name: 'Section A', count: 1 }]);
                        setView('create');
                    }}
                 >
                    Create Assessment
                 </Button>
             </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assessments.filter(a => {
             const matchesSearch = !searchFilter || a.title?.toLowerCase().includes(searchFilter.toLowerCase()) || a.type?.toLowerCase().includes(searchFilter.toLowerCase());
             if (!matchesSearch) return false;
             if (activeFilter === 'Published') return a.status === 'Published';
             if (activeFilter === 'Drafts') return a.status !== 'Published';
             if (activeFilter === 'Bookmarked') return bookmarkedIds.includes(a.id);
             return true;
          }).map((item) => (
            <motion.div whileHover={{ y: -2 }} key={item.id}>
              <Card 
                  onClick={() => handlePreview(item.id)}
                  className="cursor-pointer hover:shadow-lg hover:border-blue-400/30 transition-all duration-200 border-gray-100 dark:border-white/5 bg-white dark:bg-white/[0.03] h-full group flex flex-col rounded-xl overflow-hidden"
              >
                <CardHeader className="p-4 pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md ${
                         item.type === 'CAT' ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-500/20' :
                         item.type === 'Quiz' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' :
                         item.type === 'Exam' ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-500/20' :
                         'bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-500/20'
                      }`}>
                         {item.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-0.5 -mr-1 -mt-0.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleBookmark(item.id); }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                        >
                          <Bookmark className={`w-3.5 h-3.5 ${bookmarkedIds.includes(item.id) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground group-hover:text-amber-400 transition-colors'}`} />
                        </button>
                        <DropdownMenu>
                           <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                               <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600 rounded-lg">
                                   <MoreVertical className="w-3.5 h-3.5" />
                               </Button>
                           </DropdownMenuTrigger>
                           <DropdownMenuContent align="end" className="bg-white dark:bg-[#0f1221]">
                               <DropdownMenuItem onClick={() => handlePreview(item.id)} className="text-xs font-bold cursor-pointer">
                                   <Eye className="w-3 h-3 mr-2 text-blue-500" /> Preview
                               </DropdownMenuItem>
                               <DropdownMenuItem onClick={() => handleEdit(item.id)} className="text-xs font-bold cursor-pointer">
                                   <Edit className="w-3 h-3 mr-2 text-amber-500" /> Edit Assessment
                               </DropdownMenuItem>
                               <DropdownMenuItem className="text-xs font-bold cursor-pointer text-red-500 focus:text-red-500">
                                   <Trash2 className="w-3 h-3 mr-2" /> Delete
                               </DropdownMenuItem>
                           </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                  </div>
                  <CardTitle className="text-sm font-bold mt-2 group-hover:text-blue-600 transition-colors line-clamp-1">{item.title}</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 flex-1">
                   <div className="flex items-center gap-3 text-muted-foreground">
                      <div className="flex items-center gap-1">
                         <BookOpen className="w-3 h-3" />
                         <span className="text-[10px] font-semibold">{item.questions?.length || 0} Qns</span>
                      </div>
                      <div className="flex items-center gap-1">
                         <Layers className="w-3 h-3" />
                         <span className="text-[10px] font-semibold">{item.totalMarks} Max</span>
                      </div>
                      <div className="flex items-center gap-1">
                         <Clock className="w-3 h-3" />
                         <span className="text-[10px] font-semibold">{item.duration || '40 min'}</span>
                      </div>
                   </div>
                </CardContent>
                <div className="px-4 pb-3 flex items-center justify-between">
                   <Badge variant="outline" className={`text-[8px] px-1.5 py-0 font-bold uppercase tracking-widest rounded-md ${
                      item.status === 'Published' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' :
                      item.status === 'Graded' ? 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/20' :
                      'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-white/10'
                   }`}>
                      {item.status || 'Draft'}
                   </Badge>
                   <div className="flex items-center gap-2">
                      <span className="text-[9px] text-muted-foreground font-medium">
                         {item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleDateString() : ''}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-blue-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                   </div>
                </div>
              </Card>
            </motion.div>
          ))}
          {assessments.length <= 3 && (
          <motion.div whileHover={{ scale: 0.99 }} onClick={() => {
              // Reset Form
              setTitle('');
              setAssessmentType('CAT');
              setQuestions([{ id: '1', type: 'Short Answer', text: '', marks: 2, difficulty: 'Easy', options: [], learningObjective: '' }]);
              setStructure([{ name: 'Section A', count: 1 }]);
              setView('create');
          }} className="cursor-pointer">
            <div className="h-full min-h-[140px] rounded-xl border-2 border-dashed border-gray-100 dark:border-white/5 flex flex-col items-center justify-center gap-2 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 hover:border-blue-300 transition-all group/new">
               <div className="w-10 h-10 rounded-lg bg-gray-50 dark:bg-white/5 flex items-center justify-center text-muted-foreground group-hover/new:text-blue-600 transition-colors shadow-sm">
                  <Plus className="w-5 h-5" />
               </div>
               <span className="font-bold text-[11px] uppercase tracking-wider text-muted-foreground group-hover/new:text-blue-600">New Assessment</span>
            </div>
          </motion.div>
          )}
        </div>
        )}
        {/* Pagination */}
        {assessments.length > 0 && !searchFilter && activeFilter === 'All' && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm">Page {currentPage}</span>
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={assessments.length < assessmentsPerPage}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );

  // --- CREATE VIEW RENDER FUNCTION ---
  const renderCreateView = () => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 animate-in fade-in duration-500 max-w-5xl mx-auto px-3 sm:px-0">
      <Input type="file" className="hidden" ref={fileInputRef} accept="image/*" onChange={handleImageFile} />
      
      {/* 0. HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('dashboard')}
            title="Go back"
            aria-label="Go back"
            className="h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-all duration-300 shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
               Assessment Creator <Badge variant="outline" className="ml-2 text-primary border-primary/30 dark:border-primary/20">Advanced Mode</Badge>
            </h1>
            <p className="text-muted-foreground mt-1">Design learner-centered assessments aligned with learning outcomes.</p>
          </div>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" onClick={() => setView('dashboard')} className="rounded-xl font-bold border-gray-200 dark:border-white/10 text-muted-foreground hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all">
               Discard
            </Button>
            <Button 
                onClick={handleSave} 
                className="rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20"
                disabled={loading}
            >
               {loading ? "Saving..." : "Save Assessment"}
            </Button>
        </div>
      </div>

      {/* 1. ASSESSMENT TYPE & DETAILS */}
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5">
           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
               <CardTitle className="flex items-center gap-2">
                 <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                 Assessment Configuration
               </CardTitle>
               
               <div className="flex items-center gap-2 bg-gray-100 dark:bg-white/5 p-1 rounded-lg">
                   <button
                      onClick={() => setCreationMode('manual')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${creationMode === 'manual' ? 'bg-white dark:bg-gray-800 shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                   >
                       Manual Entry
                   </button>
                   <button
                      onClick={() => setCreationMode('ai')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${creationMode === 'ai' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                   >
                       <Sparkles className="w-3 h-3" /> Generate with AI
                   </button>
               </div>
           </div>
           <CardDescription>Select the type of validity and core details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {creationMode === 'manual' ? (
           <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
             <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label>Title / Topic</Label>
                    <Input 
                        placeholder="e.g. Grade 8 Science: Ecosystems" 
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="font-bold text-lg"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Assessment Type</Label>
                    <Select value={assessmentType} onValueChange={(v: any) => setAssessmentType(v)}>
                        <SelectTrigger className="bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-primary/30 focus:ring-primary/20 transition-all rounded-xl">
                        <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-[#0f1221] border-gray-100 dark:border-white/10 shadow-xl p-1">
                        <SelectItem value="CAT" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer transition-colors text-xs">Continuous Assessment Test (CAT)</SelectItem>
                        <SelectItem value="Assignment" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer transition-colors text-xs">Take-away Assignment</SelectItem>
                        <SelectItem value="Exam" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer transition-colors text-xs">End of Term Exam</SelectItem>
                        <SelectItem value="Quiz" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer transition-colors text-xs">Quick Quiz</SelectItem>
                        <SelectItem value="Custom" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer transition-colors text-xs">Custom Assessment type...</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
           </div>

           {/* STRUCTURE BUILDER */}
           <div className="p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-blue-600" />
                      <Label className="text-blue-900 dark:text-blue-100 font-bold">Assessment Structure & Layout</Label>
                  </div>
                  <Button variant="ghost" size="sm" onClick={addStructureRow} className="h-6 text-xs text-blue-600 hover:bg-blue-100">
                      <Plus className="w-3 h-3 mr-1" /> Add Section
                  </Button>
              </div>
              
              <div className="space-y-3">
                  {structure.map((sect, idx) => (
                      <div key={idx} className="flex gap-3 items-end">
                          <div className="flex-1 space-y-1">
                              <Label className="text-[10px] uppercase text-muted-foreground">Section Name</Label>
                              <Input 
                                value={sect.name} 
                                onChange={(e) => updateStructure(idx, 'name', e.target.value)}
                                className="bg-white dark:bg-black/20"
                              />
                          </div>
                          <div className="w-24 space-y-1">
                              <Label className="text-[10px] uppercase text-muted-foreground">Qn Count</Label>
                              <Input 
                                type="number" 
                                value={sect.count} 
                                onChange={(e) => updateStructure(idx, 'count', Number(e.target.value))}
                                className="bg-white dark:bg-black/20"
                              />
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="mb-0.5 text-red-400 hover:bg-red-50"
                            onClick={() => removeStructureRow(idx)}
                            disabled={structure.length <= 1}
                          >
                             <Trash2 className="w-4 h-4" />
                          </Button>
                      </div>
                  ))}
              </div>
           </div>
           </div>
          ) : (
             <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                 <GeneratorView 
                    addQuestionsToBank={() => {}} // Not needed for this context
                    addSavedExam={() => {}} 
                    initialExam={null}
                    savedExams={[]}
                    onCancelEdit={() => setCreationMode('manual')}
                    onQuestionsGenerated={handleQuestionsGenerated}
                 />
             </div>
          )}
        </CardContent>
      </Card>

      {/* 2. INSTRUCTIONS - ONLY VISIBLE IN MANUAL MODE */}
      {creationMode === 'manual' && (
      <>
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5">
           <CardTitle className="flex items-center gap-2">
             <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">2</span>
             Instructions & Guidelines
           </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-dashed">
                  <Label className="uppercase text-xs font-bold text-muted-foreground">Total Marks</Label>
                  <div className="text-2xl font-bold text-primary">{totalMarks}</div>
               </div>
              <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-lg border border-dashed">
                 <Label className="uppercase text-xs font-bold text-muted-foreground">Est. Duration</Label>
                 <Input 
                    placeholder="e.g. 40 Mins" 
                    className="h-8 mt-1 bg-white dark:bg-black" 
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                 />
              </div>
           </div>
           <div className="space-y-2">
              <Label>Student-Facing Instructions</Label>
              <Textarea 
                value={instructions} 
                onChange={(e) => setInstructions(e.target.value)}
                className="font-mono text-sm bg-yellow-50/50 dark:bg-yellow-900/10"
              />
           </div>
        </CardContent>
      </Card>

      {/* 3. QUESTION CREATION AREA */}
      <Card className="border-2 border-primary/10">
        <CardHeader className="bg-gray-50/50 dark:bg-white/5 pb-4">
           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
             <CardTitle className="flex items-center gap-2">
               <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">3</span>
               Question Builder
             </CardTitle>
             <Button 
                onClick={addQuestion} 
                size="sm" 
                variant="ghost" 
                className="bg-primary/10 text-primary hover:bg-primary hover:text-white shadow-none transition-all duration-300 font-bold px-4 rounded-lg flex items-center gap-2"
             >
               <Plus className="w-4 h-4" /> Add Question
             </Button>
           </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">

           {questions.map((q, idx) => {
             // Check if this is the first question of a new section
             const isSectionStart = idx === 0 || questions[idx - 1].section !== q.section;

             return (
               <div key={q.id}>
                 {isSectionStart && q.section && (
                    <div className="mb-6 mt-8 first:mt-0 flex items-center gap-4">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                        <span className="px-4 py-1.5 bg-gray-100 dark:bg-white/10 rounded-full text-sm font-bold text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-white/5">
                            {q.section}
                        </span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                    </div>
                 )}
             <div key={q.id} className="relative group border rounded-xl p-4 sm:p-6 overflow-hidden hover:shadow-md transition-shadow bg-white dark:bg-[#0f1221]">
                {/* Header Row */}
                <div className="flex gap-3 sm:gap-4 items-start mb-4">
                   <div className="flex flex-col items-center gap-1 pt-1 shrink-0">
                     <span className="font-bold text-lg text-muted-foreground/50">Q{idx + 1}</span>
                     <GripVertical className="w-4 h-4 text-muted-foreground/30 cursor-grab" />
                   </div>
                   
                   <div className="flex-1 min-w-0 grid md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                         <Input 
                           placeholder="Type your question here..." 
                           className="font-medium text-lg border-transparent hover:border-input focus:border-input transition-colors px-0 hover:px-3 focus:px-3"
                           value={q.text}
                           onChange={(e) => updateQuestion(q.id, 'text', e.target.value)}
                         />
                         
                         {/* Image Attachment Area */}
                         {/* Image Attachment Area */}
                         <div className="mt-2 text-xs flex flex-wrap gap-2 sm:gap-4 items-center">
                            {q.image ? (
                                <div className="relative inline-block mt-2">
                                    <img src={q.image} alt="Question Attachment" className="h-32 rounded-lg border border-gray-200" />
                                    <Button 
                                        size="icon" 
                                        variant="destructive" 
                                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                                        onClick={() => updateQuestion(q.id, 'image', null)}
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                </div>
                            ) : (
                                <Button 
                                    variant="link" 
                                    className="p-0 h-auto text-muted-foreground hover:text-primary flex items-center gap-1"
                                    onClick={() => triggerImageUpload(q.id)}
                                >
                                    <ImageIcon className="w-3 h-3" /> Attach Image/Start Diagram
                                </Button>
                            )}
                            
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs border-dashed text-muted-foreground hover:text-primary hover:border-primary/50"
                                onClick={() => addSubQuestion(q.id)}
                            >
                                <Plus className="w-3 h-3 mr-1" /> Add Sub-part (a, b...)
                            </Button>
                         </div>
                      </div>
                      <div>
                         <Select 
                           value={q.type} 
                           onValueChange={(v) => updateQuestion(q.id, 'type', v)}
                         >
                           <SelectTrigger className="h-8 text-[11px] font-bold bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 hover:border-primary/30 transition-all rounded-lg uppercase tracking-wider">
                              <SelectValue />
                           </SelectTrigger>
                           <SelectContent className="bg-white dark:bg-[#0f1221] border-gray-100 dark:border-white/10 shadow-xl p-1">
                              <SelectItem value="Multiple Choice" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Multiple Choice</SelectItem>
                              <SelectItem value="Short Answer" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Short Answer</SelectItem>
                              <SelectItem value="Essay" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Essay</SelectItem>
                              <SelectItem value="True/False" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">True / False</SelectItem>
                           </SelectContent>
                         </Select>
                      </div>
                   </div>
                   
                   {/* Actions */}
                   <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => moveQuestion(idx, 'up')}>
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => moveQuestion(idx, 'down')}>
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-500 hover:bg-red-50" onClick={() => removeQuestion(q.id)}>
                          <Trash2 className="w-3 h-3" />
                      </Button>
                   </div>
                </div>

                {/* Dynamic Content Based on Type */}
                <div className="pl-10 space-y-4">
                   {/* SHOW OPTIONS/ANSWER ONLY IF NO SUB-QUESTIONS (Standard Mode) */}
                   {(!q.subQuestions || q.subQuestions.length === 0) && (
                       <>
                           {q.type === 'Multiple Choice' && (
                              <div className="space-y-3">
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {q.options?.map((opt, i) => (
                                      <div key={i} className="flex items-center gap-2 group/opt">
                                         <div 
                                            className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-bold shrink-0 cursor-pointer transition-colors ${q.correctAnswerIndex === i ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-muted-foreground hover:border-green-400 hover:text-green-500'}`}
                                            onClick={() => updateQuestion(q.id, 'correctAnswerIndex', i)}
                                            title="Mark as Correct Answer"
                                         >
                                            {q.correctAnswerIndex === i ? <Check className="w-3 h-3" /> : String.fromCharCode(65+i)}
                                         </div>
                                         <Input 
                                            placeholder={`Option ${String.fromCharCode(65+i)}`} 
                                            className="h-9 text-sm" 
                                            value={opt}
                                            onChange={(e) => updateOption(q.id, i, e.target.value)}
                                         />
                                         <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-6 w-6 opacity-0 group-hover/opt:opacity-100 text-red-400 hover:bg-red-50"
                                            onClick={() => removeOption(q.id, i)}
                                            disabled={q.options?.length ? q.options.length <= 2 : true}
                                         >
                                            <X className="w-3 h-3" />
                                         </Button>
                                      </div>
                                    ))}
                                 </div>
                                 <Button variant="ghost" size="sm" className="text-xs h-6 px-2 text-primary hover:bg-primary/5" onClick={() => addOption(q.id)}>
                                    <Plus className="w-3 h-3 mr-1" /> Add Option
                                 </Button>
                              </div>
                           )}
                           
                           {q.type !== 'Multiple Choice' && (!q.subQuestions || q.subQuestions.length === 0) && (
                              <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-md">
                                 <Label className="text-xs text-muted-foreground uppercase tracking-widest mb-1 block">Marking Guide / Model Answer</Label>
                                 <Textarea 
                                   placeholder="Key points expected in the answer..." 
                                   className="h-16 text-sm resize-none border-none bg-transparent focus-visible:ring-0 px-0" 
                                   value={q.modelAnswer || ''}
                                   onChange={(e) => updateQuestion(q.id, 'modelAnswer', e.target.value)}
                                 />
                              </div>
                           )}
                       </>
                   )}

                   {/* Sub-Questions Area (Multipart) */}
                   {q.subQuestions && q.subQuestions.length > 0 && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary/20 mt-4">
                          <Label className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Sub-Questions</Label>
                          {q.subQuestions.map((sq, sqIdx) => (
                              <div key={sq.id} className="flex flex-col gap-3 p-3 sm:p-4 border border-gray-100 dark:border-white/5 rounded-xl bg-gray-50/50 dark:bg-white/5 group/sub overflow-hidden">
                                  {/* Sub-Question Header Line */}
                                  <div className="flex gap-3 items-start">
                                      <div className="w-8 h-8 flex items-center justify-center font-bold text-sm text-primary bg-white dark:bg-white/10 shadow-sm rounded border border-gray-200 dark:border-white/10">
                                          {String.fromCharCode(97 + sqIdx)}
                                      </div>
                                       <div className="flex-1 min-w-0 space-y-3">
                                           <div className="flex flex-wrap gap-2">
                                              <Input 
                                                  value={sq.text} 
                                                  onChange={(e) => updateSubQuestion(q.id, sq.id, 'text', e.target.value)}
                                                  className="h-8 text-sm bg-white dark:bg-black/20 font-medium"
                                                  placeholder={`Part (${String.fromCharCode(97 + sqIdx)}) question text...`}
                                              />
                                              
                                              {/* Sub-Question Type Selector */}
                                              <Select 
                                                 value={sq.type} 
                                                 onValueChange={(v: any) => updateSubQuestion(q.id, sq.id, 'type', v)}
                                              >
                                                <SelectTrigger className="w-[130px] h-8 text-[10px] font-bold bg-white dark:bg-white/5 uppercase tracking-wider">
                                                   <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                   <SelectItem value="Short Answer">Short Answer</SelectItem>
                                                   <SelectItem value="Multiple Choice">Multiple Choice</SelectItem>
                                                </SelectContent>
                                              </Select>

                                              <div className="w-24 relative">
                                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-bold">mks</span>
                                                  <Input 
                                                      type="number" 
                                                      value={sq.marks} 
                                                      onChange={(e) => updateSubQuestion(q.id, sq.id, 'marks', Number(e.target.value))}
                                                      className="h-8 text-sm pr-9 text-right font-bold bg-white dark:bg-black/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                  />
                                              </div>
                                              
                                              <Button 
                                                  size="icon" 
                                                  variant="ghost" 
                                                  className="h-8 w-8 text-red-400 opacity-0 group-hover/sub:opacity-100 transition-opacity hover:bg-red-50"
                                                  onClick={() => removeSubQuestion(q.id, sq.id)}
                                              >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                              </Button>
                                          </div>

                                          {/* Sub-Question Options (If Multiple Choice) */}
                                          {sq.type === 'Multiple Choice' && (
                                              <div className="space-y-2 pl-2 border-l-2 border-dashed border-gray-200 dark:border-gray-700 ml-1">
                                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                       {(sq.options || ['', '', '', '']).map((opt, optI) => (
                                                           <div key={optI} className="flex items-center gap-2 min-w-0">
                                                              <div 
                                                                 className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold cursor-pointer transition-colors ${sq.correctAnswerIndex === optI ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-white/20 hover:bg-green-200 text-muted-foreground'}`}
                                                                 onClick={() => updateSubQuestion(q.id, sq.id, 'correctAnswerIndex', optI)}
                                                                 title="Mark as Correct Answer"
                                                              >
                                                                  {sq.correctAnswerIndex === optI ? <Check className="w-2.5 h-2.5" /> : String.fromCharCode(65 + optI)}
                                                              </div>
                                                              <Input 
                                                                 value={opt} 
                                                                 onChange={(e) => updateSubOption(q.id, sq.id, optI, e.target.value)}
                                                                 className="h-7 text-xs bg-white dark:bg-black/20"
                                                                 placeholder={`Option ${String.fromCharCode(65 + optI)}`} 
                                                              />
                                                              <Button
                                                                 size="icon" variant="ghost" className="h-6 w-6 text-red-300 hover:text-red-500"
                                                                 onClick={() => removeSubOption(q.id, sq.id, optI)}
                                                              >
                                                                 <X className="w-3 h-3" />
                                                              </Button>
                                                          </div>
                                                      ))}
                                                  </div>
                                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-blue-600" onClick={() => addSubOption(q.id, sq.id)}>
                                                      <Plus className="w-3 h-3 mr-1" /> Add Option
                                                  </Button>
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          ))}
                      </div>
                   )}

                   {/* Question Metadata Row */}
                   <div className="flex flex-wrap gap-4 pt-4 border-t border-dashed mt-4">
                      <div className="flex items-center gap-2">
                         <Label className="text-xs">Marks:</Label>
                         <Input 
                            type="number" 
                            className="w-16 h-8 disabled:opacity-70 disabled:bg-gray-100 dark:disabled:bg-white/10" 
                            value={q.marks} 
                            onChange={(e) => updateQuestion(q.id, 'marks', e.target.value)}
                            disabled={!!(q.subQuestions && q.subQuestions.length > 0)}
                            title={q.subQuestions && q.subQuestions.length > 0 ? "Auto-calculated from sub-questions" : "Set marks"}
                         />
                      </div>
                      <div className="flex-1">
                         <Input 
                            placeholder="Learning Objective (e.g. Analysis)" 
                            className="h-8 text-xs border-indigo-100 bg-indigo-50/30 text-indigo-700 placeholder:text-indigo-300" 
                            value={q.learningObjective}
                            onChange={(e) => updateQuestion(q.id, 'learningObjective', e.target.value)}
                         />
                      </div>
                        <Select 
                          value={q.difficulty}
                          onValueChange={(v) => updateQuestion(q.id, 'difficulty', v)}
                       >
                          <SelectTrigger className="w-full sm:w-[100px] h-8 text-[10px] bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 hover:border-primary/30 transition-all rounded-lg font-bold uppercase tracking-tight">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#0f1221] border-gray-100 dark:border-white/10 shadow-xl p-1">
                             <SelectItem value="Easy" className="focus:bg-green-500/10 dark:focus:bg-green-500/20 cursor-pointer text-[10px] font-bold text-green-600">EASY</SelectItem>
                             <SelectItem value="Medium" className="focus:bg-amber-500/10 dark:focus:bg-amber-500/20 cursor-pointer text-[10px] font-bold text-amber-600">MEDIUM</SelectItem>
                             <SelectItem value="Hard" className="focus:bg-red-500/10 dark:focus:bg-red-500/20 cursor-pointer text-[10px] font-bold text-red-600">HARD</SelectItem>
                          </SelectContent>
                       </Select>
                   </div>
                   
                   <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span>{q.difficulty} level question - aligned to syllabus.</span>
                   </div>
                </div>
             </div>
             </div>
             );
           })}
           
            <div className="grid grid-cols-1 gap-4">
                <Button 
                   variant="ghost" 
                   className="w-full h-32 rounded-2xl border-2 border-dashed border-gray-100 dark:border-white/5 hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all duration-300 group/addbtn flex flex-col items-center justify-center gap-3" 
                   onClick={addQuestion}
                >
                   <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 flex items-center justify-center transition-colors duration-300 shadow-inner">
                      <Plus className="w-6 h-6 group-hover/addbtn:rotate-90 transition-transform duration-500" />
                   </div>
                   <div className="text-center">
                      <span className="font-bold text-sm uppercase tracking-widest block">Add Next Question</span>
                   </div>
                </Button>
            </div>
        </CardContent>
      </Card>

      {/* 4. MARKING & RUBRIC */}
      <Card>
         <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5">
           <CardTitle className="flex items-center gap-2">
             <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">4</span>
             Mark Scheme & Rubric
           </CardTitle>
           <CardDescription>Auto-generated breakdown of expectations.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
            <div className="space-y-4">
               {questions.filter(q => q.type !== 'Section').map((q, i) => (
                  <div key={q.id} className="flex flex-col gap-2 py-3 border-b border-gray-100 dark:border-white/5 last:border-0 pl-1">
                     {/* Main Question Row */}
                     <div className="flex gap-4 text-sm">
                        <span className="font-bold w-12 text-muted-foreground shrink-0">Q{i+1}:</span>
                        <div className="flex-1">
                           {/* Main Question Answer (if no sub-questions) */}
                           {(!q.subQuestions || q.subQuestions.length === 0) && (
                               <div className="text-sm">
                                   {q.type === 'Multiple Choice' && q.correctAnswerIndex !== undefined ? (
                                       <span className="text-green-700 dark:text-green-400 font-semibold">
                                           Answer: {String.fromCharCode(65 + q.correctAnswerIndex)}. {q.options?.[q.correctAnswerIndex]}
                                       </span>
                                   ) : (
                                       <div className="text-gray-800 dark:text-gray-200">
                                            <span className="font-bold text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider block mb-1">Model Answer / Expectation:</span>
                                            <div dangerouslySetInnerHTML={{ __html: formatMarkSchemeAnswer(q.modelAnswer) }} className="overflow-x-auto" />
                                       </div>
                                   )}
                               </div>
                           )}
                           {/* If subquestions exist, the main row might be empty of answer, just a header */}
                           {q.subQuestions && q.subQuestions.length > 0 && (
                               <span className="text-muted-foreground italic text-xs">See sub-part answers below.</span>
                           )}
                        </div>
                        <span className="font-bold text-primary w-12 text-right">{q.marks} mks</span>
                     </div>

                     {/* Sub-Questions Breakdown in Mark Scheme - Full treatment like main questions */}
                     {q.subQuestions?.map((sq, sqIdx) => (
                         <div key={sq.id} className="py-3 pl-8 border-l-2 border-primary/30 dark:border-primary/20 ml-4 mt-2">
                             {/* Sub-question header */}
                             <div className="flex items-start gap-3 mb-2">
                                 <span className="font-bold text-primary text-sm shrink-0">{String.fromCharCode(97+sqIdx)})</span>
                                 <div className="text-sm font-medium text-gray-800 dark:text-gray-200 flex-1">
                                     <MarkdownText>{sq.text}</MarkdownText>
                                 </div>
                                 <span className="font-bold text-primary text-sm shrink-0">{sq.marks} mks</span>
                             </div>
                             
                             {/* Sub-question answer - same formatting as main question */}
                             <div className="ml-6">
                                 {sq.type === 'Multiple Choice' && sq.correctAnswerIndex !== undefined ? (
                                     <div className="text-orange-600 dark:text-orange-400 font-semibold p-2 bg-orange-50 dark:bg-orange-900/20 rounded border-l-4 border-orange-400">
                                         Answer: {String.fromCharCode(65 + sq.correctAnswerIndex)}. {sq.options?.[sq.correctAnswerIndex]}
                                     </div>
                                 ) : (
                                     <div dangerouslySetInnerHTML={{ __html: formatMarkSchemeAnswer(sq.modelAnswer) }} className="overflow-x-auto" />
                                 )}
                             </div>
                         </div>
                     ))}
                  </div>
               ))}
            </div>
        </CardContent>
      </Card>

      {/* 5. SETTINGS & EXTRAS */}
      <div className="grid md:grid-cols-2 gap-6">
         <Card className="border-gray-100 dark:border-white/5 shadow-sm">
           <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5">
             <CardTitle className="text-base font-bold flex items-center gap-2">
               <span className="w-1.5 h-4 bg-primary rounded-full" />
               Configuration
             </CardTitle>
           </CardHeader>
           <CardContent className="space-y-4 pt-6">
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition-all group/toggle">
                 <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white dark:bg-white/10 shadow-sm border border-gray-100 dark:border-white/5 group-hover/toggle:text-blue-600 transition-colors">
                       <Sparkles size={16} />
                    </div>
                    <div>
                       <Label className="font-bold text-sm block cursor-pointer">Shuffle Questions</Label>
                       <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-tight">Randomize order for students</span>
                    </div>
                 </div>
                 <Switch className="data-[state=checked]:bg-blue-600" />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/30 dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition-all group/toggle">
                 <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white dark:bg-white/10 shadow-sm border border-gray-100 dark:border-white/5 group-hover/toggle:text-blue-600 transition-colors">
                       <FileText size={16} />
                    </div>
                    <div>
                       <Label className="font-bold text-sm block cursor-pointer">Marking Guide</Label>
                       <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-tight">Attach rubric to assessment</span>
                    </div>
                 </div>
                 <Switch defaultChecked className="data-[state=checked]:bg-blue-600" />
              </div>
           </CardContent>
         </Card>
         <Card>
           <CardHeader><CardTitle className="text-base">Teacher Reflection</CardTitle></CardHeader>
           <CardContent>
              <Textarea placeholder="Notes: How does this assess the topic? What pre-requisite knowledge is needed?" className="resize-none" />
           </CardContent>
         </Card>
      </div>

      <div className="flex justify-center pt-8 border-t border-dashed mt-8 pb-10">
         <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 px-10 rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-95"
            onClick={handleSave}
            disabled={loading}
         >
            <Save className="w-4 h-4 mr-2" /> {loading ? "Saving..." : "Save Assessment"}
         </Button>
      </div>
      </>
      )}

    </motion.div>
  );


  return (
    <div className="max-w-5xl mx-auto pb-10 px-1 sm:px-2 md:px-5">
      <AnimatePresence mode="wait">
        {view === 'dashboard' ? (
          <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
             {renderDashboardView()}
          </motion.div>
        ) : view === 'create' ? (
          <motion.div key="create" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
             {renderCreateView()}
          </motion.div>
        ) : view === 'ai-generator' ? (
          <motion.div key="ai-generator" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
           <AssessmentCreator
            assessmentId={beta2AssessmentId}
            initialAssessment={beta2InitialAssessment}
            onBack={() => {
              setBeta2AssessmentId(null);
              setBeta2InitialAssessment(null);
              setView('dashboard');
            }}
            onSaveSuccess={() => {
              setBeta2AssessmentId(null);
              setBeta2InitialAssessment(null);
              setView('dashboard');
            }}
           />
          </motion.div>
        ) : view === 'dedicated-editor' && beta2InitialAssessment ? (
          <motion.div key="dedicated-editor" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DedicatedAssessmentEditor
              examPaper={beta2InitialAssessment.beta2Source?.examPaper || beta2InitialAssessment.examPaper || beta2InitialAssessment}
              onCancel={() => {
                setBeta2AssessmentId(null);
                setBeta2InitialAssessment(null);
                setView('dashboard');
              }}
              onSave={async (updatedPaper) => {
                if (!beta2AssessmentId) return;
                try {
                  // JSON stringify/parse completely removes undefined keys automatically, which fixes Firestore rejection.
                  const cleanPaper = JSON.parse(JSON.stringify(updatedPaper));

                  if (!auth.currentUser) throw new Error("User not authenticated.");

                  const docRef = doc(db, `teaching_workspaces/${auth.currentUser.uid}/assessments/${beta2AssessmentId}`);
                  
                  // Use updateDoc for a safer specific field update, but if document might be missing beta2Source completely, 
                  // setDoc with merge: true is safer. Let's stick to setDoc but with the perfectly clean object.
                  await setDoc(docRef, { 
                     beta2Source: { examPaper: cleanPaper },
                     examPaper: cleanPaper // Also sync to root for backward compatibility if needed
                  }, { merge: true });
                  
                  // Update local state
                  setAssessments(prev => prev.map(a => 
                    a.id === beta2AssessmentId ? { ...a, beta2Source: { ...a.beta2Source, examPaper: cleanPaper }, examPaper: cleanPaper } : a
                  ));
                  
                  setBeta2AssessmentId(null);
                  setBeta2InitialAssessment(null);
                  setView('dashboard');
                } catch (error: any) {
                  console.error("Error saving assessment:", error);
                  alert("Failed to save changes: " + (error.message || "Unknown Firestore Error"));
                }
              }}
            />
          </motion.div>
        ) : (
          <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
             <AssessmentPreview 
                data={previewData} 
                onBack={() => setView('dashboard')}
                onEdit={() => {
                    handleEdit(previewData.id);
                }}
             />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IndependentAssessmentView;
